<template>
  <div class="message-carousel">
    <carousel-slide
      v-for="(slide, index) in slides"
      :key="index"
      :header="slide.header"
      :subheader="slide.subheader"
      :body="slide.body"
      :smallImageFile="slide.smallImageFile"
      :bigImageFile="slide.bigImageFile"
      :class="['message-carousel__slide', { 'message-carousel__slide--active': index === slideIndex }]"
    />
    <div class="message-carousel__controls">
      <span
        v-for="(slide, index) in slides"
        :key="index"
        :class="['message-carousel__circle', {'message-carousel__circle--active': index === slideIndex}]"
        @click="selectSlide(index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import CarouselSlide from '@/components/export/CarouselSlide.vue'
import { ref, watch } from 'vue'

const props = defineProps<{
  triggerResetTimer: boolean
}>()

const slides = [
  {
    header: 'SERATO STUDIO',
    subheader: 'Sounds',
    body: 'Looking to take your beats to the next level? Then you need Serato\'s high-quality sound pack library. ' +
      'With thousands of royalty-free samples from world-renowned producers and musicians, you can add new layers or flair to your tracks. ' +
      'Simply visit <a href="' + import.meta.env.VUE_APP_CLIENT_URI_SERATO_COM + '/studio/sounds" target="_blank">https://serato.com/studio/sounds</a> to start downloading for free.',
    smallImageFile: 'slide-one-700px-700px',
    bigImageFile: 'slide-one-900px-900px'
  },
  {
    header: 'GROW YOUR FOLLOWING',
    subheader: 'Build your brand',
    body: 'Make your videos recognisable at a glance by uploading your logo and using similar colours, filters and fonts.',
    smallImageFile: 'slide-three-700px-700px',
    bigImageFile: 'slide-three-900px-900px'
  },
  {
    header: 'TIPS AND TRICKS',
    subheader: 'Make your video pop',
    body: 'If you want your video to really stand out, try adding some effects that react to your beats. You can add an effect to individual layers for a unique look.',
    smallImageFile: 'slide-four-700px-700px',
    bigImageFile: 'slide-four-900px-900px'
  },
  {
    header: 'TIPS AND TRICKS',
    subheader: 'Get the size right <Get it right, get it tight>',
    body: 'Make sure you\'re using the correct aspect ratio for the platform you\'re posting to. Use the canvas shape toggle to export your video in different aspect ratios. That way, you\'ll make use of every pixel.',
    smallImageFile: 'slide-five-700px-700px',
    bigImageFile: 'slide-five-900px-900px'
  },
  {
    header: 'GROW YOUR FOLLOWING',
    subheader: 'Consistency is key',
    body: 'To build a fan base and get the greatest reach for your music and videos, aim to post new content every day.',
    smallImageFile: 'slide-six-700px-700px',
    bigImageFile: 'slide-six-900px-900px'
  },
  {
    header: 'TIPS AND TRICKS',
    subheader: 'Help us make the Visualizer even better',
    body: 'We want to make Visualizer the best it can be, and we need your help. If you have any suggestions for features or improvements, please submit them to us in the Serato forum. We love hearing your ideas!',
    smallImageFile: 'slide-seven-700px-700px',
    bigImageFile: 'slide-seven-900px-900px'
  }
]

const slideIndex = ref(0)
const slideDurationMilliseconds = 12000
let slideTimeout: ReturnType<typeof setTimeout>

const selectSlide = (index: number) => {
  restartSlideTimer()
  slideIndex.value = index
}

const incrementSlide = () => {
  if (slideIndex.value < slides.length - 1) {
    slideIndex.value++
  } else {
    slideIndex.value = 0
  }
  restartSlideTimer()
}

const restartSlideTimer = () => {
  clearTimeout(slideTimeout)
  slideTimeout = setTimeout(incrementSlide, slideDurationMilliseconds)
}

watch(() => props.triggerResetTimer, () => {
  slideIndex.value = 0
  restartSlideTimer()
})
</script>

<style lang="scss">
.message-carousel {
  width: 900px;
  height: 350px;
  position: relative;

  &__slide {
    position: absolute;
    transition: opacity 250ms $animationCurve;
    opacity: 0%;

    &--active {
      opacity: 100%;
      z-index: 1; // Allows links to be clicked on
    }
  }

  &__controls {
    position: absolute;
    left: 0;
    right: 0;
    padding-left: 192px;
    margin-left: auto;
    margin-right: auto;
    width: 288px;
    bottom: -28px;
    display: flex;
    gap: 8px;
    z-index: 2; // Allow this to be above the slide

    @media (max-width: 1400px) {
      padding-left: 96px;
      width: 192px;
    }
  }

  &__circle {
    cursor: pointer;
    height: 8px;
    width: 8px;
    background-color: $purple;
    border-radius: 50%;

    &--active {
      background-color: $white;
      transition: background-color 250ms $animationCurve;
    }
  }
}
</style>
